// src/lib/transitions.ts
//
// How a row of `app.bill_transition` reads to an officer.
//
// ══ WHY THIS LEDGER IS THE ONLY SOURCE FOR TWO FACTS ══
//
// `app.bill.approved_by` holds the **level 1** approver, and keeps holding it
// after a level 2 approval — deliberately, so the bill row records who let it
// through the first gate. The consequence is that the identity of the officer
// who gave FINAL approval exists nowhere except this ledger, and neither does
// the reason attached to a rejection. Until `GET /api/bills/:reference/
// transitions` existed, nothing in the product read the table at all: the
// reason the reject route *requires* a caller to supply was captured and then
// unanswerable.
//
// ══ WHY THE KIND IS TREATED AS AN OPEN STRING ══
//
// The client still has an answer for a value it does not know, for the same
// reason `lib/audit-outcome.ts` does: the value crosses JSON and is not
// validated by the compiler, and the one record of who decided what must not
// render a blank line.
//
// ★★★ WAVE 10 — AND THE PARAGRAPH ABOVE USED TO SAY *"the database CHECK admits
// exactly three values today … a lifecycle that grows a REFER step (clarification
// Q8 is open on exactly that)"*. Both halves are now out of date, and the second
// one came true:
//
//   * The CHECK admits SEVEN values. `052_billing_saga_reversal.sql` added
//     `REVERSED` (the saga's own compensating action) and 060 added
//     `REQUEST_INFO`, `RESUME` and `REFER` — Annex A 37/38's pause, its return
//     path, and the referral Q8 asked about.
//   * ⚠ **`REVERSED` WAS LIVE AND UNHANDLED FOR SEVERAL WAVES, AND THAT WAS A
//     REAL DEFECT ON A REAL SCREEN.** It fell through every switch below to the
//     default branch, so a genuine saga compensation rendered under *"this build
//     does not recognise this transition, so it cannot say what it did to the
//     bill"* — on the exact screen a reviewer opens to investigate a reversal, and
//     with a grey "unknown" dot beside it. The kind being an open string is what
//     kept that legible rather than blank, which is the mechanism working; it is
//     not a substitute for handling a value the product actually writes.
//
// So all seven are handled below, and the open default remains for the eighth.
import type { BillTransition, BillTransitionKind } from './types'

const KNOWN: ReadonlySet<string> = new Set<BillTransitionKind>([
  'APPROVE_L1',
  'APPROVE_L2',
  'REJECT',
  'REVERSED',
  'REQUEST_INFO',
  'RESUME',
  'REFER',
])

export function isKnownTransition(kind: string): kind is BillTransitionKind {
  return KNOWN.has(kind)
}

/**
 * The headline for one entry, in the past tense — this is a record of what
 * happened, not a status.
 *
 * ⚠ The level is read from the ROW, not inferred from the kind. `level` is
 * "the authorisation level the action required" (008's own header comment), so
 * a REJECT is recorded as level 1 whichever stage it happened at, and printing
 * a level next to "Rejected" would invite it to be read as "rejected at stage
 * 1" — which is not what the column means.
 */
export function transitionHeadline(transition: BillTransition): string {
  switch (transition.transition) {
    case 'APPROVE_L1':
      return 'Approved at level 1'
    case 'APPROVE_L2':
      return 'Approved at level 2: final'
    case 'REJECT':
      return 'Rejected'
    // ★★ WAVE 10 — the four that used to fall through to the raw value.
    case 'REVERSED':
      return 'Reversed by the system'
    case 'REQUEST_INFO':
      return 'Paused: information requested from the HSP'
    case 'RESUME':
      return 'Resumed: returned to its approval queue'
    case 'REFER':
      return 'Referred to a colleague'
    default:
      return transition.transition
  }
}

/**
 * ★★ WAVE 10 — the return address, as a sentence, for a `REQUEST_INFO` entry.
 *
 * ⚠ `null` when the entry is not a pause OR when it carries no address, and the
 * caller must render nothing rather than a guess. A `REQUEST_INFO` row without an
 * address cannot be written through this product —
 * `bill_transition_resume_status_iff` (060) forbids it — so its absence means
 * either an older gateway that does not send the field or a row edited directly in
 * the database. Neither is a licence to name a queue the ledger does not.
 */
export function resumeDestinationSentence(transition: BillTransition): string | null {
  if (transition.transition !== 'REQUEST_INFO') return null
  const status = transition.resumeStatus
  if (typeof status !== 'string' || status.length === 0) return null
  return status === 'AWAITING_L2'
    ? 'When it is resumed it returns to the LEVEL 2 queue: a level 1 approval is already recorded against it.'
    : status === 'AWAITING_L1'
      ? 'When it is resumed it returns to the LEVEL 1 queue: no approval has been recorded against it yet.'
      : `When it is resumed it returns to ${status}.`
}

/** What this entry did to the bill, for the line under the headline. */
export function transitionEffect(transition: BillTransition): string {
  switch (transition.transition) {
    case 'APPROVE_L1':
      return 'The bill moved to level 2 and became visible to a level 2 approver.'
    case 'APPROVE_L2':
      return 'The bill became APPROVED. This is the last step of the lifecycle.'
    case 'REJECT':
      return 'The bill was closed. Nothing is paid, and the reason below is what the healthcare service provider is told.'
    case 'REVERSED':
      return 'The intake saga unwound its own earlier step. No officer decided this, and the bill it names was compensated rather than refused.'
    case 'REQUEST_INFO':
      return 'The bill left its approval queue and no approval or rejection can be recorded against it until it is resumed. It is waiting on the HSP, not on this office.'
    case 'RESUME':
      return 'The bill went back into the queue it was paused out of, and can be decided again by whoever holds that queue’s authority.'
    case 'REFER':
      return 'Nothing about the bill changed. A colleague was asked to look at it, and it stayed in whatever queue it was already in: a referral never costs a bill its place in the line.'
    default:
      return 'This build does not recognise this transition, so it cannot say what it did to the bill. The record itself is unaffected: it is shown here exactly as stored.'
  }
}

/**
 * ★★ WAVE 10 — two tones added, and `REVERSED` moved off `unknown`.
 *
 * ⚠ A pause and a referral share the `pause` tone rather than getting one each.
 * The tone is a 12px dot; five distinguishable dot colours that all pass contrast
 * against the same background is a harder problem than it is worth, and the
 * headline beside every dot already says which it is. Colour carries no
 * information here that the text does not — the rule this whole app is built to
 * (WCAG 1.4.1).
 *
 * ⚠ `REVERSED` is `system`, NOT `unknown`. It used to be `unknown`, which put a
 * grey "this build does not recognise it" dot on a transition the product itself
 * writes.
 */
export type TransitionTone = 'approve' | 'reject' | 'pause' | 'system' | 'unknown'

export function transitionTone(transition: BillTransition): TransitionTone {
  if (transition.transition === 'REJECT') return 'reject'
  if (transition.transition === 'APPROVE_L1' || transition.transition === 'APPROVE_L2') return 'approve'
  if (transition.transition === 'REQUEST_INFO' || transition.transition === 'RESUME' || transition.transition === 'REFER') {
    return 'pause'
  }
  if (transition.transition === 'REVERSED') return 'system'
  return 'unknown'
}

/**
 * ★★ WAVE 10 — who a `REFER` entry names, or `null`.
 *
 * ⚠ Returns `null` for BOTH "not a referral" and "withheld from your role", and
 * the caller renders nothing in either case. Distinguishing them on screen would
 * tell a reader who may not see identities that there IS an identity to see, which
 * is the same disclosure one step removed.
 */
export function referralTargetName(transition: BillTransition): string | null {
  if (transition.transition !== 'REFER') return null
  const name = transition.referredToDisplayName
  return typeof name === 'string' && name.length > 0 ? name : null
}

/**
 * Whether this entry should show a reason block.
 *
 * ⚠ Driven by whether a reason IS PRESENT, never by the kind. The database
 * enforces the correlation — `CHECK ((transition = 'REJECT') = (reason IS NOT
 * NULL))` in 008 — but a CHECK is not a type, and this is the client. Keying on
 * the kind would hide a reason that arrived on an unexpected row, which on a
 * ledger is the one thing worth seeing.
 */
export function hasReason(transition: BillTransition): transition is BillTransition & { reason: string } {
  return typeof transition.reason === 'string' && transition.reason.trim().length > 0
}

/**
 * A REJECT with no reason — a state the database forbids and this client still
 * reports rather than rendering an empty quote block.
 */
export function isReasonMissing(transition: BillTransition): boolean {
  return transition.transition === 'REJECT' && !hasReason(transition)
}
